import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Chip,
  ChipProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';

import { useGetChainRateRules, useInvalidateChainRateRules } from '~/hooks/apis/reservation/useGetChainRateRules';

import ReservationService from '~/services/ReservationService';

import DeleteRuleDialog from './DeleteRuleDialog';
import RateRuleModal, { RateRule } from './RateRuleModal';

interface Props {
  chainId: string;
}

interface ImportResultRow {
  row: number;
  status: 'created' | 'skipped' | 'error';
  name?: string;
  match_type?: string;
  match_value?: string;
  reason?: string;
}

interface ImportResult {
  total: number;
  created: number;
  skipped: number;
  errors: number;
  rows: Array<ImportResultRow>;
}

const matchTypeColor = (type: string): ChipProps['color'] => {
  switch (type) {
    case 'suffix':
      return 'info';
    case 'prefix':
      return 'error';
    case 'exact':
      return 'warning';
    case 'regex':
      return 'success';
    default:
      return 'default';
  }
};

export default function ChainRateRulesTab({ chainId }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RateRule | null>(null);
  const [deleteRule, setDeleteRule] = useState<RateRule | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const { data: rules = [], isLoading, isError, error } = useGetChainRateRules(chainId);
  const invalidateRules = useInvalidateChainRateRules();

  const handleCreate = () => {
    setEditingRule(null);
    setModalOpen(true);
  };

  const handleEdit = (rule: RateRule) => {
    setEditingRule(rule);
    setModalOpen(true);
  };

  const handleSubmit = async (data: Record<string, unknown>) => {
    setSaving(true);
    try {
      if (editingRule) {
        await ReservationService.updateChainRateRule(chainId, editingRule.id, data);
        enqueueSnackbar('Rate rule updated', { variant: 'success' });
      } else {
        await ReservationService.createChainRateRule(chainId, data);
        enqueueSnackbar('Rate rule created', { variant: 'success' });
      }
      setModalOpen(false);
      invalidateRules(chainId);
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to save rate rule', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRule) {
      return;
    }
    try {
      await ReservationService.deleteChainRateRule(chainId, deleteRule.id);
      enqueueSnackbar('Rate rule deleted', { variant: 'success' });
      setDeleteRule(null);
      invalidateRules(chainId);
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to delete rate rule', { variant: 'error' });
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedUploadFile) {
      return;
    }
    setUploading(true);
    setImportResult(null);
    try {
      const response: { result: ImportResult } = await ReservationService.importChainRateRules(
        chainId,
        selectedUploadFile,
      );
      setImportResult(response.result);
      enqueueSnackbar(
        `Imported ${response.result.created} rate rules, skipped ${response.result.skipped}, errors ${response.result.errors}`,
        { variant: response.result.errors ? 'warning' : 'success' },
      );
      setUploadModalOpen(false);
      setSelectedUploadFile(null);
      invalidateRules(chainId);
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to upload rate rules', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const columns: Array<GridColDef<RateRule>> = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      display: 'flex',
      minWidth: 180,
    },
    {
      field: 'match_type',
      headerName: 'Match',
      width: 100,
      display: 'flex',
      renderCell: (params) => <Chip label={params.value} color={matchTypeColor(params.value)} size="small" />,
    },
    {
      field: 'match_value',
      headerName: 'Value',
      width: 140,
      display: 'flex',
      renderCell: (params) => <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{params.value}</span>,
    },
    {
      field: 'rate_category',
      headerName: 'Category',
      width: 100,
      display: 'flex',
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'package' ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'rate_type',
      headerName: 'Rate Type',
      width: 80,
      display: 'flex',
    },
    {
      field: 'board_type',
      headerName: 'Board Type',
      width: 110,
      display: 'flex',
      renderCell: (params) =>
        params.value ? (
          <Chip label={params.value} size="small" variant="outlined" color="info" />
        ) : (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            —
          </Box>
        ),
    },
    {
      field: 'markup',
      headerName: 'Markup / Commission %',
      width: 180,
      display: 'flex',
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_value, row) => {
        if (row.rate_type === 'net' && row.markup != null) {
          return Number(row.markup);
        }
        if (row.rate_type === 'gross' && row.commission != null) {
          return Number(row.commission);
        }
        return null;
      },
      renderCell: (params) => {
        if (params.value == null) {
          return '—';
        }
        const label = params.row.rate_type === 'gross' ? 'commission' : 'markup';
        return `${params.value}% ${label}`;
      },
    },
    {
      field: 'is_blocked',
      headerName: 'Status',
      width: 100,
      display: 'flex',
      renderCell: (params) => {
        if (!params.row.is_active) {
          return <Chip label="Inactive" color="warning" size="small" />;
        }
        if (params.row.is_blocked) {
          return <Chip label="Blocked" color="error" size="small" />;
        }
        return <Chip label="Active" color="success" size="small" />;
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteRule(params.row);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Chain rate rules classify incoming channel manager defined rate codes for this chain.
        </Typography>
        <Typography variant="body2">
          Bulk upload accepts a CSV file up to 1,000 rows and creates rules that do not already exist for the same match
          type and match value. Use headers:
        </Typography>
        <Typography variant="body2" component="pre" sx={{ my: 1, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
          name,match_type,match_value,rate_category,rate_type,markup,commission,board_type,is_blocked,notes,is_active
        </Typography>
        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
          <li>Match type is prefix, suffix, exact, or regex.</li>
          <li>Rate category is standard or package.</li>
          <li>Rate type is net or gross.</li>
          <li>Net rows require markup and gross rows require commission unless the row is blocked.</li>
          <li>Board type is optional: roomonly, breakfast, halfboard, fullboard, or allinclusive.</li>
          <li>Other fields are optional.</li>
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 1.5 }}>
          Match types
        </Typography>
        <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
          <li>prefix matches rate codes that start with the match value, for example ADV matches ADV123.</li>
          <li>suffix matches rate codes that end with the match value, for example BB matches RATE-BB.</li>
          <li>exact matches only rate codes that are identical to the match value, for example BAR matches BAR.</li>
          <li>
            regex matches rate codes using the match value as a regular expression, for example ^BAR-\\d+$ matches
            BAR-123.
          </li>
        </Box>
      </Alert>

      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setUploadModalOpen(true)}>
          Bulk Upload Rules
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Add Rule
        </Button>
      </Stack>

      {importResult && (
        <Alert severity={importResult.errors ? 'warning' : 'success'} sx={{ mb: 2 }}>
          <Typography variant="subtitle2">
            Bulk upload complete: {importResult.created} created, {importResult.skipped} skipped, {importResult.errors}{' '}
            errors from {importResult.total} rows.
          </Typography>
          {importResult.rows.some((row) => row.status !== 'created') && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
              {importResult.rows
                .filter((row) => row.status !== 'created')
                .slice(0, 10)
                .map((row) => (
                  <li key={`${row.row}-${row.status}-${row.match_value}`}>
                    Row {row.row}: {row.status}
                    {row.reason ? ` - ${row.reason}` : ''}
                  </li>
                ))}
            </Box>
          )}
        </Alert>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Failed to load rate rules'}
        </Alert>
      )}

      <Box sx={{ width: '100%' }}>
        <DataGrid
          columns={columns}
          rows={rules}
          loading={isLoading}
          autoHeight
          pageSizeOptions={[25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          onRowClick={(params) => handleEdit(params.row)}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-columnHeaderTitle': { whiteSpace: 'normal', lineHeight: 1.2 },
            '& .MuiDataGrid-columnHeader': { alignItems: 'flex-end' },
          }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
          disableColumnFilter
          disableDensitySelector
        />
      </Box>

      <RateRuleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        existingRule={editingRule}
        loading={saving}
      />

      <Dialog
        open={uploadModalOpen}
        onClose={() => {
          if (!uploading) {
            setUploadModalOpen(false);
            setSelectedUploadFile(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Upload Rules</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Choose a CSV file, then submit it to create new chain rate rules. Existing duplicates are skipped.
            </Typography>
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={uploading}>
              {selectedUploadFile ? selectedUploadFile.name : 'Choose CSV File'}
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(event) => {
                  setSelectedUploadFile(event.target.files?.[0] ?? null);
                  event.target.value = '';
                }}
              />
            </Button>
            {selectedUploadFile && (
              <Typography variant="body2" color="text.secondary">
                Ready to upload: {selectedUploadFile.name}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadModalOpen(false);
              setSelectedUploadFile(null);
            }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleBulkUpload} disabled={!selectedUploadFile || uploading}>
            {uploading ? 'Uploading...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteRuleDialog
        open={!!deleteRule}
        ruleName={deleteRule?.name || ''}
        matchInfo={
          deleteRule
            ? `${deleteRule.match_type}: ${deleteRule.match_value}${
                deleteRule.tier ? ` (tier: ${deleteRule.tier})` : ''
              }`
            : ''
        }
        onClose={() => setDeleteRule(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
